import { alpha, Box, styled, SxProps, Theme, Typography } from '@mui/material';
import { useMemo } from 'react';

import {
  CaseRotationStatus,
  caseRotationStatusMap,
  NEW_CASES_ROTATION_GROUP,
} from '#entities';

import {
  EXCLUDED_FROM_HIGHLIGHT,
  useAppBehavior,
  useData,
} from '../data-context';
import { useSelectIsRotationOn } from '../widgetManager';

import { RENDER_SEQUENCE } from './config';

const StatusWrapper = styled(Box)(({ theme: { spacing, utils } }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.4)',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)', // Safari support
  overflowX: 'auto',
  borderRadius: spacing(1.5),
  width: 'max-content',
  maxWidth: '90%',
  gap: utils.clamp(8, 24, 'md-4k'),
  padding: spacing(0.5, 1),
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  margin: '0 auto',

  '&::-webkit-scrollbar': {
    display: 'none',
  },
}));

type StatusItemProps = {
  active?: boolean; // bright text (count>0 or selected)
  selected?: boolean; // background pill (explicit highlight)
  disabled?: boolean;
  muted?: boolean;
};

const excludedProps: PropertyKey[] = [
  'active',
  'selected',
  'muted',
  'disabled',
];

const StatusItem = styled(Box, {
  shouldForwardProp: p => !excludedProps.includes(p),
})<StatusItemProps>(
  ({ active, selected, muted, disabled, theme: { palette, spacing } }) => ({
    display: 'flex',
    padding: spacing(1),
    borderRadius: spacing(1.5),
    alignItems: 'center',
    whiteSpace: 'nowrap',
    cursor: disabled ? 'default' : 'pointer',

    // colors
    color: selected || active ? palette.common.white : palette.grey[400],
    backgroundColor: selected
      ? alpha(palette.common.white, 0.08)
      : 'transparent',

    // emphasis
    opacity: muted && !selected ? 0.35 : selected || active ? 1 : 0.6,
    filter: muted && !selected ? 'saturate(0.85)' : 'none',

    // smooth feel
    transition:
      'background-color 160ms ease, color 160ms ease, opacity 160ms ease, filter 160ms ease',

    '&:hover': disabled
      ? {}
      : { color: palette.common.white, opacity: 1, filter: 'none' },
  }),
);

const StyledTypography = styled(Typography, {
  shouldForwardProp: prop => prop !== 'active' && prop !== 'selected',
})<{ active?: boolean; selected?: boolean }>(({ theme: { utils } }) => ({
  fontWeight: 400,
  fontSize: utils.clamp(12, 18),
}));

interface CaseRotationStatusesBarProps {
  sx?: SxProps<Theme>;
  onlyShowActive?: boolean;
}

export const CaseRotationStatusesBar = ({
  sx,
  onlyShowActive = false,
}: CaseRotationStatusesBarProps) => {
  const { filteredActiveCases, filteredNewCases } = useData();
  const { toggleStatus, highlightStatus } = useAppBehavior();
  const rotationOn = useSelectIsRotationOn();

  const statusCounts = useMemo((): Record<CaseRotationStatus, number> => {
    const counts: Record<CaseRotationStatus, number> = Object.keys(
      caseRotationStatusMap,
    ).reduce(
      (acc, k) => {
        acc[k as CaseRotationStatus] = 0;
        return acc;
      },
      {} as Record<CaseRotationStatus, number>,
    );

    counts[NEW_CASES_ROTATION_GROUP] = filteredNewCases.length;

    for (const { status } of filteredActiveCases) {
      if (status in caseRotationStatusMap) {
        counts[status as CaseRotationStatus] += 1;
      }
    }
    return counts;
  }, [filteredActiveCases, filteredNewCases.length]);

  return (
    <StatusWrapper sx={sx}>
      {RENDER_SEQUENCE.map(item => {
        if (item.kind === 'group') {
          const total = item.members.reduce(
            (sum, key) => sum + (statusCounts[key] ?? 0),
            0,
          );
          if (onlyShowActive && total === 0) {
            return null;
          }

          // pick first member that has count > 0 and is not excluded
          const representative = item.members.find(
            m => (statusCounts[m] ?? 0) > 0 && !EXCLUDED_FROM_HIGHLIGHT.has(m),
          );
          const hasSelection = Boolean(highlightStatus);
          const selected =
            !!highlightStatus && item.members.includes(highlightStatus);
          const active = rotationOn ? selected : total > 0 || selected;
          const disabled = !selected && (total === 0 || !representative);

          return (
            <StatusItem
              key={item.key}
              active={active}
              selected={selected}
              muted={hasSelection && !selected}
              disabled={disabled}
              role="button"
              aria-pressed={selected}
              aria-disabled={disabled}
              onClick={
                disabled
                  ? undefined
                  : () => representative && toggleStatus(representative)
              }
            >
              <StyledTypography sx={{ mr: 0.5 }}>{item.label}</StyledTypography>
              <StyledTypography>{total}</StyledTypography>
            </StatusItem>
          );
        }

        // single status item
        const count = statusCounts[item.status] ?? 0;
        if (onlyShowActive && count === 0) {
          return null;
        }
        const hasSelection = Boolean(highlightStatus);
        const selected = !!highlightStatus && highlightStatus === item.status;
        const active = rotationOn ? selected : count > 0 || selected;
        const disabled =
          !selected &&
          (count === 0 || EXCLUDED_FROM_HIGHLIGHT.has(item.status));

        return (
          <StatusItem
            key={item.status}
            active={active}
            selected={selected}
            muted={hasSelection && !selected} // fade others
            disabled={disabled}
            role="button"
            aria-pressed={selected}
            aria-disabled={disabled}
            onClick={disabled ? undefined : () => toggleStatus(item.status)}
          >
            <StyledTypography sx={{ mr: 0.5 }}>
              {caseRotationStatusMap[item.status]}
            </StyledTypography>
            <StyledTypography>{count}</StyledTypography>
          </StatusItem>
        );
      })}
    </StatusWrapper>
  );
};
